// ============================================================================
// Cognito ベース認証ラッパ（Amplify v6 modular）
// ============================================================================
// Phase 4a で Firebase Auth から移行。
// Hosted UI + Google IdP（Phase 4a で前倒し追加）を使った Authorization Code + PKCE。
// 既存 main.ts から呼ばれるインターフェースは下記 4 関数を維持する：
//   - configureAuth()       … アプリ起動時の Amplify configure
//   - signInWithGoogle()    … Google IdP 経由の Hosted UI へリダイレクト
//   - signOut()             … Cognito ログアウト + Hosted UI セッションのクリア
//   - watchAuthState(cb)    … 認証状態の監視（Hub 経由）
//   - getCurrentIdToken()   … API 呼び出し用の id token を返す
// ----------------------------------------------------------------------------

import { Amplify } from "aws-amplify";
import {
  signInWithRedirect,
  signOut as amplifySignOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

// chloe-pwa 内で使う Guardian の表現。Firebase の User と互換のフィールドだけ持たせる。
export interface GuardianUser {
  userId: string;        // Cognito sub
  email: string | null;  // 表示用
  displayName: string | null; // Cognito name 属性
}

// アプリで使う簡易セッション。実際の token はメモリ取り回しに留め、ID token は API クライアントが都度取得する。
let currentUser: GuardianUser | null = null;
let configured = false;

function buildRedirectUrl(path: string): string {
  if (typeof window === "undefined") return path;
  const origin = window.location.origin.replace(/\/$/, "");
  return `${origin}${path}`;
}

/**
 * アプリ初期化時に呼ぶ。Vite の import.meta.env で Cognito 設定を読み込み Amplify v6 に渡す。
 */
export function configureAuth(): void {
  if (configured) return;

  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || "";
  const userPoolClientId = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID || "";
  const hostedUiDomain = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN || "";

  if (!userPoolId || !userPoolClientId || !hostedUiDomain) {
    throw new Error(
      "Cognito 設定が見つかりません。VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_USER_POOL_CLIENT_ID / VITE_COGNITO_HOSTED_UI_DOMAIN を確認してください。"
    );
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: hostedUiDomain, // 例: chloe-dev-guardians.auth.ap-northeast-1.amazoncognito.com
            scopes: ["openid", "email", "profile"],
            redirectSignIn: [buildRedirectUrl("/auth/callback")],
            redirectSignOut: [buildRedirectUrl("/")],
            responseType: "code", // Authorization Code + PKCE
          },
          email: true,
        },
      },
    },
  });

  configured = true;
}

/**
 * Google IdP 経由のサインインを開始。Hosted UI にリダイレクトされる。
 * 戻り値は無し（ページ遷移）。コールバックは /auth/callback で Amplify が処理する。
 */
export async function signInWithGoogle(): Promise<void> {
  await signInWithRedirect({ provider: "Google" });
}

/**
 * サインアウト。Cognito の Hosted UI セッションも一緒にクリアする global ログアウト。
 */
export async function signOut(): Promise<void> {
  await amplifySignOut({ global: true });
  currentUser = null;
}

/**
 * Firebase の onAuthStateChanged 相当。Amplify Hub の "auth" チャンネルで監視。
 * 初回呼び出し時に現在のセッションを評価して即座に callback を呼ぶ。
 */
export function watchAuthState(
  callback: (user: GuardianUser | null) => void
): () => void {
  // 初回評価
  refreshCurrentUser().then((user) => callback(user)).catch(() => callback(null));

  const unsubscribe = Hub.listen("auth", async ({ payload }) => {
    switch (payload.event) {
    case "signedIn":
    case "tokenRefresh":
    case "signInWithRedirect": {
      const user = await refreshCurrentUser();
      callback(user);
      break;
    }
    case "signedOut":
    case "tokenRefresh_failure":
    case "signInWithRedirect_failure": {
      currentUser = null;
      callback(null);
      break;
    }
    default:
      // ignore
    }
  });

  return unsubscribe;
}

/**
 * id_token のクレームから現在ユーザを取り直す。
 * OIDC スコープ（openid email profile）で発行された id_token には
 * email / name / sub が含まれるので、Cognito GetUser API（要 aws.cognito.signin.user.admin
 * スコープ）を叩く必要は無い。Federated（Google IdP）ユーザでも安全に動く。
 */
async function refreshCurrentUser(): Promise<GuardianUser | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as
      | { sub?: string; email?: string; name?: string; "cognito:username"?: string }
      | undefined;
    if (!payload || !payload.sub) {
      currentUser = null;
      return null;
    }
    currentUser = {
      userId: payload.sub,
      email: payload.email ?? null,
      displayName: payload.name ?? null,
    };
    return currentUser;
  } catch (_err) {
    currentUser = null;
    return null;
  }
}

/**
 * API リクエスト時に Authorization ヘッダに載せる ID token を返す。
 * セッションが無ければ null。Amplify は内部でリフレッシュも面倒を見る。
 */
export async function getCurrentIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch (_err) {
    return null;
  }
}

/**
 * 同期的に最新ユーザを取得（getCurrentUser を呼ばずに直近キャッシュを返す）。
 * UI レンダリングで都度 await したくない場面用。
 */
export function getCachedUser(): GuardianUser | null {
  return currentUser;
}
